import { Component, Input, forwardRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface TagsComponentData {
  selectedTags: string[];        // 已選擇的標籤
  availableTags: string[];       // 可選的預設標籤
  allowCustomInput: boolean;     // 是否允許自定義輸入
  placeholder?: string;          // 輸入框提示文字
}

@Component({
  selector: 'app-form-tags',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section">
      <label [attr.for]="id" [class.required-field]="required">{{ label }}</label>
      
      <div class="tags-container">
        <!-- 已選擇的標籤顯示區域 -->
        <div class="selected-tags-display">
          <div class="tag-chip" *ngFor="let tag of selectedTags">
            <span>{{ tag }}</span>
            <button type="button" class="tag-remove-btn" (click)="removeTag(tag)" title="移除標籤">
              ✕
            </button>
          </div>
          
          <!-- 自定義輸入框 -->
          <input
            type="text"
            class="tag-input"
            [placeholder]="selectedTags.length === 0 ? placeholder : ''"
            [(ngModel)]="customInput"
            (keyup.enter)="addCustomTag()"
            (keyup.escape)="showDropdown = false"
            (focus)="showDropdown = true"
            (input)="onInputChange()"
            [id]="id"
            [name]="name"
          />
          
          <!-- 下拉箭頭 -->
          <button 
            type="button" 
            class="dropdown-toggle"
            (click)="toggleDropdown()"
            [class.open]="showDropdown"
          >
            <i class="fa fa-chevron-down"></i>
          </button>
        </div>
        
        <!-- 下拉選單 -->
        <div class="dropdown-options" [class.show]="showDropdown">
          <div 
            class="dropdown-option"
            *ngFor="let tag of getAvailableOptions()"
            (click)="addTag(tag)"
          >
            {{ tag }}
          </div>
          
          <!-- 如果有自定義輸入且不在列表中，顯示新增選項 -->
          <div 
            class="dropdown-option custom-option"
            *ngIf="customInput && !availableTags.includes(customInput) && !selectedTags.includes(customInput)"
            (click)="addCustomTag()"
          >
            <i class="fa fa-plus"></i> 新增 "{{ customInput }}"
          </div>
          
          <!-- 沒有可選項目時的提示 -->
          <div class="dropdown-empty" *ngIf="getAvailableOptions().length === 0 && !customInput">
            沒有可選的標籤
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormTagsComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormTagsComponent implements ControlValueAccessor {
  @Input() label: string = '標籤';
  @Input() id: string = 'tags-input';
  @Input() name: string = 'tags-input';
  @Input() placeholder: string = '選擇或輸入標籤...';
  @Input() required: boolean = false;
  
  @Input() set options(value: string[] | undefined) {
    if (value) {
      this.availableTags = [...value];
    }
  }
  
  selectedTags: string[] = [];
  availableTags: string[] = [];
  customInput: string = '';
  showDropdown: boolean = false;

  // ControlValueAccessor callback 函數
  onChange = (value: string[]) => {};
  onTouched = () => {};

  // 輸入變更處理
  onInputChange(): void {
    // 當有輸入時顯示下拉選單
    if (this.customInput.trim()) {
      this.showDropdown = true;
    }
  }

  // 點擊外部時關閉下拉選單
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.tags-container')) {
      this.showDropdown = false;
    }
  }

  // 新增標籤
  addTag(tag: string): void {
    if (tag && !this.selectedTags.includes(tag)) {
      this.selectedTags.push(tag);
      this.updateValue();
      this.customInput = '';
      this.showDropdown = false;
    }
  }

  // 移除標籤
  removeTag(tag: string): void {
    const index = this.selectedTags.indexOf(tag);
    if (index > -1) {
      this.selectedTags.splice(index, 1);
      this.updateValue();
    }
  }

  // 新增自定義標籤
  addCustomTag(): void {
    const trimmedInput = this.customInput.trim();
    if (trimmedInput) {
      this.addTag(trimmedInput);
    }
  }

  // 切換下拉選單顯示
  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  // 取得可選的標籤選項（排除已選擇的）
  getAvailableOptions(): string[] {
    return this.availableTags.filter(tag => !this.selectedTags.includes(tag));
  }

  // 更新值並觸發變更
  private updateValue(): void {
    this.onChange(this.selectedTags);
    this.onTouched();
  }

  // ControlValueAccessor 實作
  writeValue(value: string[]): void {
    this.selectedTags = value || [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在此實作禁用狀態
  }
}